// src/api/auth.ts

import { BASE_URL } from "../utils/config";
import { getAuthToken } from "../services/authService"; // Assuming getAuthToken is moved to a service
import {
	CreateAccountResponse,
	LoginResponse,
	User,
	innerCircleState,
} from "../types/api";
import { messageToast } from "../components/common/toastMessage"; // Direct UI call - consider refactoring

/**
 * Attempts to log in a user with username and password.
 * @param username - User's email/username.
 * @param password - User's password.
 * @returns A promise resolving to the LoginResponse data if successful.
 * @throws {Error} If the login request fails or returns an error.
 */
export async function loginUser(
	username: string,
	password: string,
	options?: { claimInnerCircle?: boolean; requireTempMerge?: boolean },
): Promise<LoginResponse> {
	const params = new URLSearchParams();
	if (options?.claimInnerCircle) params.set("claim_inner_circle", "true");
	if (options?.requireTempMerge) params.set("require_temp_merge", "true");
	const query = params.toString();
	const url = `${BASE_URL}/auth/login${query ? `?${query}` : ""}`;
	const body = `username=${encodeURIComponent(
		username,
	)}&password=${encodeURIComponent(password)}`;

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			body: body,
			credentials: "include",
		});

		if (!response.ok) {
			// Provide more specific feedback if possible
			const errorText = await response.text();
			console.error(`Login failed: ${response.status}`, errorText);
			if (options?.claimInnerCircle || options?.requireTempMerge) {
				let data: unknown;
				try {
					data = JSON.parse(errorText);
				} catch (error) {
					if (!(error instanceof SyntaxError)) throw error;
					console.warn("Claim sign-in returned a non-JSON error response:", error);
				}
				const detail =
					typeof data === "object" && data !== null && "detail" in data
						? data.detail
						: undefined;
				throw new Error(
					typeof detail === "string" && detail
						? detail
						: "Could not merge your account. Please try again.",
				);
			}
			if (response.status === 400 || response.status === 401) {
				throw new Error("Invalid email or password. Please try again");
			}
			// Any login carrying a guest session merges strictly server-side, so
			// surface the merge failure message (409/503) rather than a status code.
			if (response.status === 409 || response.status === 503) {
				try {
					const detail = (JSON.parse(errorText) as { detail?: unknown })?.detail;
					if (typeof detail === "string" && detail) throw new Error(detail);
				} catch (error) {
					if (!(error instanceof SyntaxError)) throw error;
				}
			}
			throw new Error(`Login failed with status: ${response.status}`);
		}

		const data: LoginResponse = await response.json();
		return data;
	} catch (error: any) {
		console.error("Error during login:", error);
		// Re-throw the specific error message if it was generated above
		throw new Error(error.message || "An error occurred during login.");
	}
}

export class LoginCodeRequestError extends Error {
	constructor(
		message: string,
		public readonly retryAfterSeconds?: number,
	) {
		super(message);
		this.name = "LoginCodeRequestError";
	}
}

export async function requestLoginCode(email: string): Promise<number> {
	const response = await fetch(`${BASE_URL}/auth/login-code`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email }),
		credentials: "include",
	});

	if (response.ok) {
		const payload: unknown = await response.json();
		if (
			typeof payload === "object" &&
			payload !== null &&
			"retry_after" in payload &&
			typeof payload.retry_after === "number"
		) {
			return payload.retry_after;
		}
		return 60;
	}

	let message = "Could not request a sign-in code. Please try again.";
	try {
		const payload: unknown = await response.json();
		if (
			typeof payload === "object" &&
			payload !== null &&
			"detail" in payload &&
			typeof payload.detail === "string"
		) {
			message = payload.detail;
		}
	} catch (error) {
		if (!(error instanceof SyntaxError)) throw error;
	}
	const retryAfterHeader = response.headers.get("Retry-After");
	const retryAfterSeconds = retryAfterHeader
		? Number.parseInt(retryAfterHeader, 10)
		: undefined;
	throw new LoginCodeRequestError(
		message,
		Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : undefined,
	);
}

export async function loginWithCode(
	email: string,
	code: string,
	options?: { claimInnerCircle?: boolean; requireTempMerge?: boolean },
): Promise<LoginResponse> {
	const response = await fetch(`${BASE_URL}/auth/login-code/verify`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({
			email,
			code,
			claim_inner_circle: options?.claimInnerCircle ?? false,
			require_temp_merge: options?.requireTempMerge ?? false,
		}),
		credentials: "include",
	});

	if (!response.ok) {
		let detail: unknown;
		try {
			const payload: unknown = await response.json();
			if (typeof payload === "object" && payload !== null && "detail" in payload) {
				detail = payload.detail;
			}
		} catch (error) {
			if (!(error instanceof SyntaxError)) throw error;
		}

		if (typeof detail === "string" && detail) {
			throw new Error(detail);
		}
		if (response.status === 401) {
			throw new Error("Invalid or expired code. Please request a new one.");
		}
		throw new Error(`Code sign-in failed with status: ${response.status}`);
	}

	return (await response.json()) as LoginResponse;
}

export async function fetchInnerCircleState(): Promise<innerCircleState> {
	const url = `${BASE_URL}/users/inner-circle-state`;
	const token = getAuthToken();

	if (!token) {
		throw new Error("No authentication token found.");
	}

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			const errorText = await response.text();
			console.error(
				`Fetch inner circle state failed: ${response.status}`,
				errorText,
			);
			throw new Error(`Failed to fetch inner circle state: ${response.status}`);
		}

		const data: innerCircleState = await response.json();

		return data;
	} catch (error: any) {
		console.error("Error fetching inner circle state:", error);
		throw new Error(error.message || "An error occurred during login.");
	}
}

/**
 * Fetches the current authenticated user's details.
 * @returns A promise resolving to the User data if authenticated.
 * @throws {Error} If the request fails or the user is not authenticated (based on response status).
 */
export async function fetchCurrentUser(): Promise<User> {
	const url = `${BASE_URL}/auth/me`;
	const token = getAuthToken();

	if (!token) {
		throw new Error("No authentication token found.");
	}

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			// Distinguish between unauthorized and other errors
			if (response.status === 401 || response.status === 403) {
				throw new Error("Unauthorized"); // Specific error for auth failure
			}
			const errorText = await response.text();
			console.error(`Fetch current user failed: ${response.status}`, errorText);
			throw new Error(`Failed to fetch user data: ${response.status}`);
		}

		const data: User = await response.json();
		return data;
	} catch (error: any) {
		console.error("Error fetching current user:", error);
		// Propagate specific "Unauthorized" error
		if (error.message === "Unauthorized") {
			throw error;
		}
		throw new Error(
			error.message || "An error occurred while fetching user data.",
		);
	}
}

/**
 * Creates a new user account.
 * @param userData - Object containing email, full_name, password, etc.
 * @returns A promise resolving to the CreateAccountResponse data.
 * @throws {Error} If the account creation fails.
 */
export async function createUserAccount(userData: {
	email: string;
	full_name: string;
	password: string;
	is_temp: boolean | null; // Allow null
	temporary_email: string | null; // Allow null
}): Promise<CreateAccountResponse> {
	const url = `${BASE_URL}/users/join`;

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(userData),
			credentials: "include",
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({
				detail: `Account creation failed with status: ${response.status}`,
			}));
			console.error(`Account creation failed: ${response.status}`, errorData);
			// Use detail message from API if available
			throw new Error(
				errorData.detail || `Account creation failed: ${response.status}`,
			);
		}

		const data: CreateAccountResponse = await response.json();
		return data;
	} catch (error: any) {
		console.error("Error creating account:", error);
		throw new Error(
			error.message || "An error occurred during account creation.",
		);
	}
}

/**
 * Sends a request to reset the user's password.
 * @param email - The email address of the user requesting the reset.
 * @returns A promise resolving to the API response (structure depends on backend).
 * @throws {Error} If the request fails.
 */
export async function requestPasswordReset(email: string): Promise<any> {
	const url = `${BASE_URL}/auth/reset-password`;
	const body = `email=${encodeURIComponent(email)}`;

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			body: body,
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({
				detail: `Password reset request failed: ${response.status}`,
			}));
			console.error(
				`Password reset request failed: ${response.status}`,
				errorData,
			);
			throw new Error(
				errorData.detail || `Password reset request failed: ${response.status}`,
			);
		}

		return await response.json(); // Return the success response body
	} catch (error: any) {
		console.error("Error requesting password reset:", error);
		throw new Error(
			error.message || "An error occurred while requesting password reset.",
		);
	}
}

/**
 * Submits the new password using a reset token.
 * @param token - The password reset token received via email.
 * @param password - The new password.
 * @param passwordConfirm - The confirmation of the new password.
 * @returns A promise resolving to the API response.
 * @throws {Error} If the password reset submission fails.
 */
export async function submitNewPassword(
	token: string,
	password: string,
	passwordConfirm: string,
): Promise<any> {
	const url = `${BASE_URL}/auth/reset-password/${token}`;
	const data = {
		password: password,
		password_confirm: passwordConfirm,
	};
	const body = new URLSearchParams(data).toString();

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			body: body,
		});

		// Check if response is OK first
		if (!response.ok) {
			const errorData = await response.json().catch(() => ({
				message: `Password reset submission failed: ${response.status}`,
			}));
			console.error(
				`Password reset submission failed: ${response.status}`,
				errorData,
			);
			throw new Error(
				errorData.message || `Password reset submission failed: ${response.status}`,
			);
		}

		// If OK, parse the JSON
		const responseData = await response.json();

		// Check for application-level success indicator within the response
		if (responseData.success === false && responseData.message) {
			console.warn("Password reset API reported failure:", responseData.message);
			throw new Error(responseData.message); // Throw the specific message from the backend
		}

		return responseData; // Return the full success response
	} catch (error: any) {
		console.error("Error submitting new password:", error);
		// Ensure the specific error message is thrown
		throw new Error(
			error.message || "An error occurred while submitting the new password.",
		);
	}
}

/**
 * Submits a new password via the post-claim "set password" flow.
 *
 * Mirrors {@link submitNewPassword} but targets the dedicated
 * `/auth/set-password-after-claim/{token}` endpoint, which never issues
 * an auth cookie. The caller is expected to render an inline
 * "return to the original tab" confirmation instead of redirecting.
 */
export async function submitSetPasswordAfterClaim(
	token: string,
	password: string,
	passwordConfirm: string,
): Promise<any> {
	const url = `${BASE_URL}/auth/set-password-after-claim/${encodeURIComponent(
		token,
	)}`;
	const body = new URLSearchParams({
		password,
		password_confirm: passwordConfirm,
	}).toString();

	const response = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/x-www-form-urlencoded" },
		body,
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({
			message: `Set password failed: ${response.status}`,
		}));
		throw new Error(
			errorData.message || `Set password failed: ${response.status}`,
		);
	}

	const data = await response.json();
	if (data.success === false && data.message) {
		throw new Error(data.message);
	}
	return data;
}

// Note: Social login initiation is typically handled by redirecting the browser,
// so API calls might only be needed for the callback phase (handled server-side)
// or potentially to get the redirect URLs themselves.

/**
 * Gets the redirect URL for a specific social login provider.
 * @param service - The social login service ('google' or 'facebook').
 * @returns The full redirect URL.
 */
export function getSocialLoginUrl(service: "google" | "facebook"): string {
	// Consider fetching these from an API endpoint if they become dynamic
	switch (service) {
		case "google":
			return `${BASE_URL}/auth/social/google`;
		case "facebook":
			return `${BASE_URL}/auth/social/facebook`;
		default:
			console.warn(`Unsupported social login service: ${service}`);
			return "#not_implemented"; // Fallback
	}
}

export interface CheckEmailResponse {
	exists: boolean;
	auth_methods: Array<"password" | "google" | "facebook">;
	needs_leaderboard_name: boolean;
}

/**
 * Checks whether an account exists for the given email and returns the
 * supported authentication methods. Used by the View Recording claim modal.
 */
export async function checkEmail(email: string): Promise<CheckEmailResponse> {
	const url = `${BASE_URL}/auth/check-email`;
	const response = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email }),
		credentials: "include",
	});

	if (!response.ok) {
		throw new Error(`Email check failed with status ${response.status}`);
	}

	return (await response.json()) as CheckEmailResponse;
}

/**
 * Converts the active temporary account into a permanent one with the
 * provided email address. Triggers a password-set email server side.
 */
export async function claimTempAccount(
	email: string,
	marketing_consent: boolean,
): Promise<LoginResponse> {
	const url = `${BASE_URL}/auth/claim-temp`;
	const response = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email, marketing_consent }),
		credentials: "include",
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({
			detail: `Claim failed with status: ${response.status}`,
		}));
		throw new Error(
			errorData.detail || `Claim failed with status ${response.status}`,
		);
	}

	return (await response.json()) as LoginResponse;
}

/**
 * Re-sends the "set your password" email for a permanent account that
 * was created via the temp-claim flow but hasn't finished password setup.
 */
export async function resendSetPasswordEmail(email: string): Promise<void> {
	const url = `${BASE_URL}/auth/resend-set-password`;
	const response = await fetch(url, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email }),
		credentials: "include",
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({
			detail: `Resend failed with status: ${response.status}`,
		}));
		throw new Error(
			errorData.detail || `Resend failed with status ${response.status}`,
		);
	}
}
